if (!customElements.get('media-gallery')) {
  // Shared across every <media-gallery> on the page (a PDP can also have the
  // quick-view one), so photoswipe.js is fetched at most once per page.
  let photoSwipeLoader = null;

  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: ['.product__media-item'],
        };

        this.sliderInstance = false;
        this.thumbsInstance = false;
        this.mediaToggleInitialized = false;
        this.variantFiltered = false;
        // Stores the variant key applied by filterOnInitialLoad so that the first
        // setActiveMedia call from product-info (which duplicates the same variant)
        // can be skipped — preventing a redundant repaint / blink on page load.
        this._pendingInitialFilterKey = null;
      }

      // Produces a stable string key from a variant's option values for comparison.
      _variantKey(variant) {
        return [variant.option1, variant.option2, variant.option3]
          .map(o => (o || '').toLowerCase().trim())
          .join('|');
      }

      connectedCallback() {
        // Set layout immediately — filterOnInitialLoad needs it before inView fires
        this.mediaLayout = this.dataset.mediaLayout;
        // 'alt' = alt-text tag filtering (theme setting), 'none' = setting on but nothing to
        // filter (default-variant product), 'legacy' = featured_media + fuzzy alt fallback.
        this.variantMediaMode = this.dataset.variantMediaMode || 'legacy';
        this._parseProductSkus();
        this._parseVariantExclusiveMediaIds();
        // Filter variant images right away so the browser never paints the wrong
        // set of images, regardless of when the IntersectionObserver fires
        this.filterOnInitialLoad();
        // Cancel off-screen mobile carousel images as early as possible (at connect,
        // not when the gallery scrolls into view) so they never compete with the
        // LCP image for bandwidth. Safe no-op on desktop / if run twice.
        this.deferOffscreenImagesOnMobile();
        FoxTheme.Motion.inView(this, this.init.bind(this));
      }

      // Alt mode: every SKU of this product, normalized like the alt tags. A tag group that
      // contains one of these is a SKU group (see _altTagsMatch).
      _parseProductSkus() {
        let skus = [];
        try {
          skus = JSON.parse(this.dataset.variantSkus || '[]') || [];
        } catch (e) {}
        this._productSkus = new Set(skus.map(sku => this._normalizeAltTag(sku)).filter(Boolean));
      }

      _parseVariantExclusiveMediaIds() {
        const raw = this.dataset.variantExclusiveMediaIds || '';
        this._variantExclusiveMediaIds = raw
          .split(',')
          .map(id => parseInt(id, 10))
          .filter(id => !Number.isNaN(id));
      }

      init() {
        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        this.context = this.dataset.context;
        this.setSliderOptions();

        // For grid-mix-2-3, apply initial width classes before filtering to prevent layout shift.
        // Skip if filterOnInitialLoad already ran in connectedCallback (variantFiltered = true),
        // because applying classes to all items (including hidden ones) would undo that work.
        if (this.mediaLayout === 'grid-mix-2-3' && !this.variantFiltered) {
          this.applyInitialGridWidths();
        }

        // Filter images on initial load — skip if already done in connectedCallback
        if (!this.variantFiltered) {
          this.filterOnInitialLoad();
        }

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.updateMediaLayout.bind(this);
        const mqlTablet = window.matchMedia('(max-width: 1024px)');
        mqlTablet.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();

        if (this.enableImageZoom) {
          this.initImageZoom();
        }

        this.initMediaToggle();

        // Initial re-indexing for grid layouts
        if (this.mediaLayout === 'grid-mix-2-3') {
          // Ensure the gallery starts in collapsed state (showing max 7 images)
          this.classList.remove('media-gallery--expanded');
          this.reindexVisibleItems();
        }

        // On mobile the gallery is a horizontal carousel, but native lazy-loading
        // does not defer its off-screen slides — they all download up front and
        // starve the LCP image. Defer them and hydrate on demand instead.
        this.deferOffscreenImagesOnMobile();
      }

      // Mobile-only: keep the first visible gallery image loaded (LCP) and defer
      // the rest (src -> data-src, cancelling any in-flight fetch), then hydrate
      // each via IntersectionObserver as it approaches the viewport. Desktop is
      // untouched — there the grid genuinely shows every image at once.
      deferOffscreenImagesOnMobile() {
        try {
          if (!window.matchMedia('(max-width: 767px)').matches) return;
          if (typeof IntersectionObserver === 'undefined') return;

          const items = this.querySelectorAll('.product__media-list .product__media-item');
          const toObserve = [];
          let keptFirstVisible = false;

          items.forEach((item) => {
            const img = item.querySelector('img');
            if (!img) return;

            const isHidden =
              item.classList.contains('variant-hidden') ||
              item.classList.contains('hidden') ||
              item.style.display === 'none';

            // Keep the first visible image loaded — it is the LCP candidate.
            if (!isHidden && !keptFirstVisible) {
              keptFirstVisible = true;
              return;
            }
            // Leave variant-hidden / already-deferred images to the existing logic.
            if (isHidden) return;
            if (img.classList.contains('deferred-img') || !img.getAttribute('src')) return;

            // De-hydrate so the browser stops/never fetches it now.
            img.dataset.src = img.getAttribute('src');
            if (img.getAttribute('srcset')) img.dataset.srcset = img.getAttribute('srcset');
            if (img.getAttribute('sizes')) img.dataset.sizes = img.getAttribute('sizes');
            img.classList.add('deferred-img');
            img.removeAttribute('srcset');
            img.removeAttribute('src');
            toObserve.push(img);
          });

          if (!toObserve.length) return;

          // Only hydrate a slide as it actually enters the viewport. No horizontal
          // preload margin, so on the mobile 1-up carousel exactly one image loads
          // on first paint; the next hydrates as the user swipes to it. (Vertical
          // margin is kept for any product shown as a vertical scroll.)
          const io = new IntersectionObserver(
            (entries, obs) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting) {
                  this._hydrateImg(entry.target);
                  obs.unobserve(entry.target);
                }
              });
            },
            { root: null, rootMargin: '200px 0px' }
          );
          toObserve.forEach((img) => io.observe(img));
        } catch (e) {
          /* never block gallery init on this enhancement */
        }
      }

      applyInitialGridWidths() {
        // Apply width classes to all media items before variant filtering
        // This prevents layout shift on initial load for grid-mix-2-3 layout
        const allMediaItems = this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)');
        
        allMediaItems.forEach((item, index) => {
          const position = index + 1; // 1-based position
          const cyclePosition = ((position - 1) % 5) + 1;
          
          if (cyclePosition === 1 || cyclePosition === 2) {
            item.classList.add('grid-width-50');
          } else if (cyclePosition === 3 || cyclePosition === 4 || cyclePosition === 5) {
            item.classList.add('grid-width-33');
          }
        });
      }

      filterOnInitialLoad() {
        if (this.variantMediaMode === 'none') {
          this.classList.add('variant-filtered');
          this.variantFiltered = true;
          return;
        }

        let currentVariant = null;

        // Method 0: Server-rendered data attribute — always available immediately in
        // connectedCallback because it is baked into the HTML by Liquid.
        // This avoids any timing race where the product form hasn't been parsed yet.
        const encodedOptions = this.dataset.initialVariantOptions;
        if (encodedOptions) {
          const opts = encodedOptions.split('|').map(o => o.trim()).filter(o => o && o !== 'undefined' && o !== '');
          if (opts.length) {
            currentVariant = {
              option1: opts[0] || null,
              option2: opts[1] || null,
              option3: opts[2] || null,
              sku: this.dataset.initialVariantSku || null,
            };
          }
        }

        const initialMediaId = this.dataset.initialVariantMediaId;
        if (currentVariant && initialMediaId) {
          currentVariant.featured_media = { id: parseInt(initialMediaId, 10) };
        }

        // Method 1–3: JS fallbacks for contexts where the data attribute is absent
        // (e.g. quick-view overlays, section previews, etc.)
        if (!currentVariant) {
          const productInfo = this.closest('product-info');
          if (!productInfo) {
            this.showAllMedia();
            this.classList.add('variant-filtered');
            this.variantFiltered = true;
            return;
          }

          // Method 1: product-info's currentVariant property (set after its own init)
          if (productInfo.currentVariant) {
            currentVariant = productInfo.currentVariant;
          }

          // Method 2: inline JSON inside variant-selects
          if (!currentVariant) {
            const selectedVariantElement = productInfo.querySelector('variant-selects [data-selected-variant]');
            if (selectedVariantElement && selectedVariantElement.innerHTML) {
              try {
                currentVariant = JSON.parse(selectedVariantElement.innerHTML);
              } catch (e) {}
            }
          }

          // Method 3: URL ?variant= param → look up in product form JSON
          if (!currentVariant) {
            const variantId = new URLSearchParams(window.location.search).get('variant');
            if (variantId) {
              const productForm = productInfo.querySelector('form[is="product-form"]');
              if (productForm && productForm.dataset.product) {
                try {
                  const productData = JSON.parse(productForm.dataset.product);
                  if (productData && productData.variants) {
                    currentVariant = productData.variants.find(v => v.id == variantId);
                  }
                } catch (e) {}
              }
            }
          }
        }

        if (currentVariant) {
          this._pendingInitialFilterKey = this._variantKey(currentVariant);
          // Always filter by alt text for correctness, but protect the first 2 images
          // (position 0 & 1) from being hidden on initial load — they are already painted
          // as eager/high-priority LCP images and hiding them would cause a visible flash.
          this.filterMediaByVariant(currentVariant, false, true);
        } else {
          // If no variant found, show all images (fallback)
          this.showAllMedia();
        }

        // Mark as filtered (may already be set server-side — harmless to set again)
        this.classList.add('variant-filtered');
        this.variantFiltered = true;
      }

      // Hydrate a deferred image (data-src only, no src) so the browser fetches it.
      _hydrateImg(img) {
        if (!img || img.src || !img.dataset.src) return;
        if (img.dataset.srcset) img.srcset = img.dataset.srcset;
        if (img.dataset.sizes) img.sizes = img.dataset.sizes;
        img.src = img.dataset.src;
      }

      showAllMedia() {
        const allMediaItems = this.querySelectorAll('.product__media-item');
        const allThumbnailItems = this.querySelectorAll('.product__thumbs-item');

        allMediaItems.forEach(item => {
          // Hydrate any deferred images before revealing
          const deferredImg = item.querySelector('img.deferred-img');
          if (deferredImg) this._hydrateImg(deferredImg);
          item.style.display = '';
          item.classList.remove('hidden', 'variant-hidden', 'grid-width-50', 'grid-width-33', 'grid-width-100');
          item.classList.add('variant-visible');
          item.style.removeProperty('width');
          // Clean up show more button state
          item.removeAttribute('data-hidden-by-show-more');
        });

        allThumbnailItems.forEach(item => {
          const deferredImg = item.querySelector('img.deferred-img');
          if (deferredImg) this._hydrateImg(deferredImg);
          item.style.display = '';
          item.classList.remove('hidden', 'variant-hidden');
          item.classList.add('variant-visible');
        });

        // Reset to collapsed state (for grid-mix-2-3 layout)
        if (this.mediaLayout === 'grid-mix-2-3') {
          this.classList.remove('media-gallery--expanded');
        }

        // Re-index visible items after showing all
        this.reindexVisibleItems();

        // Keep the people-interested overlay on the first visible item
        this.repositionPeopleInterestedOverlay();
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);
        const mediaItemGapMobile = parseInt(this.dataset.mediaItemGapMobile);

        this.sliderOptions = {
          init: false,
          slidesPerView: this.enableMobileThumbnails ? 1 : 'auto',
          spaceBetween: mediaItemGapMobile,
          loop: false,
          rewind: true,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: false,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap,
            },
          },
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'progressbar',
          },
          threshold: 2,
        };

        this.thumbsOptions = {
          slidesPerView: 4,
          breakpoints: {
            461: {
              slidesPerView: 5,
              direction: 'horizontal',
            },
          },
          spaceBetween: mediaItemGapMobile,
          loop: false,
          freeMode: true,
          watchSlidesProgress: true,
          threshold: 2,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap,
            },
          },
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              768: {
                direction: 'vertical',
                slidesPerView: 'auto',
                spaceBetween: mediaItemGap,
              },
            };
            break;
          case 'carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              1024: {
                slidesPerView: 5,
              },
              1536: {
                slidesPerView: 7,
              },
            };
            break;
        }
      }

      // Desktop show-more toggle is styled/visible from 1025px up (see theme.css).
      // Below that breakpoint, always reveal all media — the toggle is hidden in CSS.
      isDesktopMediaToggleViewport() {
        return window.matchMedia('(min-width: 1025px)').matches;
      }

      updateMediaLayout() {
        if (window.matchMedia('(max-width: 1024px)').matches) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }

        if (this.mediaLayout === 'grid-mix-2-3' || this.mediaLayout === 'grid-mix-2-1') {
          this.updateShowMoreButton();
        }
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions);
            this.thumbsInstance.init();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 2,
            };
          }

          // // Rewind-to-first/last only makes sense with more than one real slide —
          // // with a single slide there's nothing to wrap to. Decided dynamically per
          // // init since media count can vary per product and changes after variant
          // // filtering re-runs this same slider instance.
          // const realSlideCount = this.querySelectorAll(
          //   '[id^="Slider-Gallery"] .product__media-item:not(.swiper-slide-duplicate)'
          // ).length;
          // this.sliderOptions.rewind = realSlideCount > 1;

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs,
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();
        }
      }

      destroySlider() {
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy();
          this.sliderInstance = false;
        }
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== 'object') {
          this.thumbsInstance = new window.FoxTheme.Carousel(this.selectors.thumbnails, this.thumbsOptions);
          this.thumbsInstance.init();
        }
      }

      // Perf: photoswipe.js is ~89 KB unzipped and the lightbox is only ever used
      // if someone actually taps zoom, so neither the library nor the lightbox is
      // built during init(). Only the click delegate is registered here (cheap);
      // the library is fetched on first intent, or on the click itself if the tap
      // beats the prefetch. Same intent-based pattern as MenuDrawer.hydrate in
      // assets/header.js.
      initImageZoom() {
        FoxTheme.utils.addEventDelegate({
          selector: '.js-photoswipe--zoom',
          context: this,
          handler: (event, media) => {
            if (media.dataset.mediaType !== 'image') return;
            const index = Number(media.dataset.mediaIndex) || 0;
            this.ensureLightbox()
              .then(() => this.lightbox.loadAndOpen(index))
              .catch((error) => console.warn('[media-gallery] image zoom unavailable', error));
          },
        });

        // Warm it up before the tap. These three all bubble, which delegation
        // needs — pointerenter/mouseenter and focus do not.
        ['pointerover', 'touchstart', 'focusin'].forEach((eventName) => {
          FoxTheme.utils.addEventDelegate({
            selector: '.js-photoswipe--zoom',
            context: this,
            event: eventName,
            handler: () => {
              this.ensureLightbox().catch(() => {
                /* the click handler reports and retries */
              });
            },
          });
        });
      }

      // Memoised: intent-prefetch and an immediate tap converge on one promise.
      // Cleared on failure so a later attempt retries rather than sticking.
      ensureLightbox() {
        if (!this._lightboxReady) {
          this._lightboxReady = this.loadPhotoSwipe()
            .then(() => this.setupLightbox())
            .catch((error) => {
              this._lightboxReady = null;
              throw error;
            });
        }
        return this._lightboxReady;
      }

      loadPhotoSwipe() {
        if (window.FoxTheme.PhotoSwipeLightbox) return Promise.resolve();

        if (!photoSwipeLoader) {
          const src = window.FoxTheme.assets && window.FoxTheme.assets.photoswipe;
          if (!src) {
            return Promise.reject(new Error('FoxTheme.assets.photoswipe is not set'));
          }
          photoSwipeLoader = new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = src;
            script.onload = resolve;
            script.onerror = () => {
              photoSwipeLoader = null;
              reject(new Error(`Failed to load ${src}`));
            };
            document.head.appendChild(script);
          });
        }

        return photoSwipeLoader;
      }

      setupLightbox() {
        let dataSource = [];
        const allMedia = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
        if (allMedia) {
          allMedia.forEach((media) => {
            switch (media.dataset.mediaType) {
              case 'model':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('product-model').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'video':
              case 'external_video':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('video-element').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'image':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  src: media.dataset.src,
                  width: media.dataset.pswpWidth,
                  height: media.dataset.pswpHeight,
                  mediaType: media.dataset.mediaType,
                });
                break;
            }
          });
        }

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
        });

        this.lightbox.addFilter('thumbEl', (thumbEl, { id }, index) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el;
              }
            }
          }

          return thumbEl;
        });

        this.lightbox.addFilter('placeholderSrc', (placeholderSrc, { data: { id } }) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el.src;
              }
            }
          }

          return placeholderSrc;
        });

        this.lightbox.on('change', () => {
          window.pauseAllMedia(this);
        });

        // Lock body scroll when lightbox opens and compensate for Windows scrollbar
        let _savedScrollY = 0;
        let _savedBodyPaddingRight = '';
        this.lightbox.on('beforeOpen', () => {
          _savedScrollY = window.scrollY || window.pageYOffset || 0;
          _savedBodyPaddingRight = document.body.style.paddingRight;
          // Calculate scrollbar width (non-zero on Windows when scrollbar is visible)
          const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
          document.documentElement.style.overflow = 'hidden';
          if (scrollbarWidth > 0) {
            document.body.style.paddingRight = scrollbarWidth + 'px';
          }
        });

        // Store the current index before closing
        let lastLightboxIndex = null;
        this.lightbox.on('close', () => {
          if (this.lightbox.pswp) {
            lastLightboxIndex = this.lightbox.pswp.currIndex;
          }
        });

        // Update slider after lightbox is destroyed
        this.lightbox.on('destroy', () => {
          // Unlock body scroll and restore scrollbar compensation
          document.documentElement.style.overflow = '';
          document.body.style.paddingRight = _savedBodyPaddingRight;
          // Restore scroll position to where user was before opening the popup
          window.scrollTo({ top: _savedScrollY, behavior: 'instant' });

          if (this.sliderInstance && this.sliderInstance.slider && lastLightboxIndex !== null) {
            const slider = this.sliderInstance.slider;
            const targetIndex = lastLightboxIndex;
            // currIndex from lightbox is the index in dataSource array
            // which should match the slider index since both are created from the same media items in the same order
            if (targetIndex >= 0 && targetIndex < slider.slides.length && slider.activeIndex !== targetIndex) {
              // Use requestAnimationFrame to ensure DOM is ready
              requestAnimationFrame(() => {
                slider.slideTo(targetIndex, 0);
              });
            }
            lastLightboxIndex = null;
          }
        });

        this.lightbox.on('pointerDown', (e) => {
          if (this.lightbox.pswp.currSlide.data.mediaType != 'image') {
            e.preventDefault();
          }
        });

        this.lightbox.on('uiRegister', () => {
          if (!this.onlyImage) {
            this.lightbox.pswp.ui.registerElement({
              name: 'next',
              ariaLabel: 'Next slide',
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.next();
              },
            });
            this.lightbox.pswp.ui.registerElement({
              name: 'prev',
              ariaLabel: 'Previous slide',
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.prev();
              },
            });
          }
          this.lightbox.pswp.ui.registerElement({
            name: 'close-zoom',
            ariaLabel: 'Close zoom image',
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, el) => {
              this.lightbox.pswp.close();
            },
          });
        });

        this.lightbox.init();
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;

          if (slides[activeIndex]) {
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }

          // // Add classes with a delay to avoid affecting Lighthouse LCP measurement
          // setTimeout(() => {
          //   // Add .active class to .product__media-gallery-viewer-wrap
          //   const viewerWrap = this.querySelector('.product__media-gallery-viewer-wrap');
          //   if (viewerWrap) {
          //     viewerWrap.classList.add('active');
          //   }

          //   // Add .hidden class to .preload-hero
          //   const preloadHero = this.querySelector('.preload-hero');
          //   if (preloadHero) {
          //     preloadHero.classList.add('hidden');
          //   }
          // }, 5000);
        });
      }

      handleSlideChange() {
        this.sliderInstance.slider.on('realIndexChange', (swiper) => {
          const { slides, activeIndex, thumbs } = swiper;

          if (thumbs.swiper) {
            thumbs.swiper.slideTo(activeIndex);
          }

          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);

            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      setActiveMedia(variant) {
        if (!variant) return;

        // If this is the first call from product-info and it matches the variant
        // already applied by filterOnInitialLoad, skip the whole update.
        // This prevents a redundant DOM repaint that causes the blink on page refresh.
        if (this._pendingInitialFilterKey !== null) {
          const isSame = this._pendingInitialFilterKey === this._variantKey(variant);
          this._pendingInitialFilterKey = null; // consume — only skip once
          if (isSame) return;
        }

        if (this.variantMediaMode === 'none') return;

        // Ensure variant-filtered class is added
        this.classList.add('variant-filtered');
        this.variantFiltered = true;

        // Filter images by alt-text tags (show/hide)
        this.filterMediaByVariant(variant, true);

        // Reorder visible items: variant-matched images first, then general images.
        // This mirrors the server-side Liquid ordering done for the initial variant.
        this.reorderMediaByVariant(variant);

        // Reset slider to first visible slide after filtering + reordering
        if (this.sliderInstance && this.sliderInstance.slider) {
          requestAnimationFrame(() => {
            try {
              this.sliderInstance.slider.update();
              this.sliderInstance.slider.updateSlides();
              this.sliderInstance.slider.updateProgress();
              this.sliderInstance.slider.updateSlidesClasses();
              this.sliderInstance.slider.slideTo(0, 0, false);
            } catch (e) {
              console.warn('Error updating slider in setActiveMedia:', e);
            }
          });

          if (this.thumbsInstance && typeof this.thumbsInstance === 'object' && this.thumbsInstance.slider) {
            requestAnimationFrame(() => {
              try {
                this.thumbsInstance.slider.update();
                this.thumbsInstance.slider.slideTo(0, 0, false);
              } catch (e) {
                console.warn('Error updating thumbnails slider:', e);
              }
            });
          }
        }
      }

      filterMediaByVariant(variant, skipSliderUpdate = false, protectFirstTwo = false) {
        if (!variant) {
          this.showAllMedia();
          if (!skipSliderUpdate) {
            this.updateSliderAfterFilter();
          }
          return;
        }

        // Get all media items
        const allMediaItems = this.querySelectorAll('.product__media-item');
        const allThumbnailItems = this.querySelectorAll('.product__thumbs-item');
        
        // Extract variant option values (e.g., ["Red", "Large"])
        const variantOptions = [];
        if (variant.option1) variantOptions.push(variant.option1.toLowerCase().trim());
        if (variant.option2) variantOptions.push(variant.option2.toLowerCase().trim());
        if (variant.option3) variantOptions.push(variant.option3.toLowerCase().trim());

        // If no variant options, show all images
        if (variantOptions.length === 0) {
          this.showAllMedia();
          this.updateSliderAfterFilter();
          return;
        }

        let hasMatchingImages = false;
        // Alt mode: thumbnails follow their main item (thumbnail alt attributes are
        // double-escaped by image_tag, so "&" in a tag would not match there).
        const visibleMediaIds = new Set();

        // Filter main gallery images
        allMediaItems.forEach(item => {
          const img = item.querySelector('img');
          if (!img || this._isAltModeNonImage(item)) {
            // For non-image media (video, model), show them
            visibleMediaIds.add(String(item.dataset.mediaId));
            item.style.display = '';
            item.classList.remove('hidden', 'variant-hidden');
            item.classList.add('variant-visible');
            // Clean up show more button state when showing items
            item.removeAttribute('data-hidden-by-show-more');
            return;
          }

          const altText = (img.getAttribute('alt') || '').toLowerCase();
          const mediaId = item.dataset.mediaId;
          const shouldShow = this.shouldShowMediaForVariant(altText, variantOptions, variant, mediaId);

          const mediaIndex = parseInt(item.dataset.mediaIndex, 10);
          const isProtected = protectFirstTwo && mediaIndex < 2;

          if (shouldShow || isProtected) {
            // Hydrate deferred images (no src yet) before revealing
            this._hydrateImg(img);
            item.style.display = '';
            item.classList.remove('hidden', 'variant-hidden');
            item.classList.add('variant-visible');
            // Clean up show more button state when showing items
            item.removeAttribute('data-hidden-by-show-more');
            hasMatchingImages = true;
            visibleMediaIds.add(String(mediaId));
          } else {
            item.style.display = 'none';
            item.classList.add('hidden', 'variant-hidden');
            item.classList.remove('variant-visible');
            // Clean up show more button state when hiding items
            item.removeAttribute('data-hidden-by-show-more');
            // Remove grid position and width classes from hidden items
            item.classList.remove('grid-pos-1-2', 'grid-pos-3-5', 'grid-pos-6-7', 'grid-pos-8-10', 'grid-width-50', 'grid-width-33', 'grid-width-100');
            item.style.removeProperty('width');
          }
        });

        // Filter thumbnail images
        allThumbnailItems.forEach(item => {
          const img = item.querySelector('img');
          if (!img) {
            item.style.display = '';
            item.classList.remove('hidden', 'variant-hidden');
            item.classList.add('variant-visible');
            return;
          }

          const altText = (img.getAttribute('alt') || '').toLowerCase();
          const mediaId = item.dataset.target || item.dataset.mediaId;
          const shouldShow = this.variantMediaMode === 'alt'
            ? visibleMediaIds.has(String(mediaId))
            : this.shouldShowMediaForVariant(altText, variantOptions, variant, mediaId);

          if (shouldShow) {
            // Hydrate deferred thumbnail images before revealing
            this._hydrateImg(img);
            item.style.display = '';
            item.classList.remove('hidden', 'variant-hidden');
            item.classList.add('variant-visible');
          } else {
            item.style.display = 'none';
            item.classList.add('hidden', 'variant-hidden');
            item.classList.remove('variant-visible');
          }
        });

        // If no matching images found, show all images (fallback)
        if (!hasMatchingImages) {
          this.showAllMedia();
        }

        // Reset to collapsed state when variant changes (for grid-mix-2-3 layout)
        if (this.mediaLayout === 'grid-mix-2-3') {
          this.classList.remove('media-gallery--expanded');
        }

        // Re-index visible items for proper grid layout
        this.reindexVisibleItems();

        // Keep the people-interested overlay on the first visible item
        this.repositionPeopleInterestedOverlay();

        if (!skipSliderUpdate) {
          this.updateSliderAfterFilter();
        }
      }

      /**
       * Reorders DOM items so variant-matched images come before general images.
       * Called after filterMediaByVariant() on each variant change.
       * Buckets: variant-matched → general (no "|") + non-image media → other-variant (hidden).
       * Uses appendChild() which moves existing nodes — no cloning, minimal reflow.
       */
      reorderMediaByVariant(variant) {
        const mediaList = this.querySelector('[id^="Slider-Gallery"]');
        const thumbList = this.querySelector('[id^="Slider-Thumbnails"]');
        if (!mediaList) return;

        const variantOptions = [];
        if (variant.option1) variantOptions.push(variant.option1.toLowerCase().trim());
        if (variant.option2) variantOptions.push(variant.option2.toLowerCase().trim());
        if (variant.option3) variantOptions.push(variant.option3.toLowerCase().trim());

        if (variantOptions.length === 0) return;

        const allItems = Array.from(
          mediaList.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')
        );

        const variantMatched = [];
        const general = [];
        const otherVariant = [];
        const altKeys = this.variantMediaMode === 'alt' ? this._variantAltKeys(variant) : null;
        // Alt mode: the variant featured image is mandatory and leads the gallery
        const featured = [];

        allItems.forEach(item => {
          const img = item.querySelector('img');
          if (!img || this._isAltModeNonImage(item)) {
            // Non-image media (video, 3D model) — always treat as general
            general.push(item);
            return;
          }
          const altText = (img.getAttribute('alt') || '').toLowerCase();

          if (altKeys) {
            const tags = this._parseAltTags(altText);
            if (this._isVariantFeaturedMedia(item.dataset.mediaId, variant)) featured.push(item);
            else if (!tags) general.push(item);
            else if (this._altTagsMatch(tags, altKeys)) variantMatched.push(item);
            else otherVariant.push(item);
            return;
          }

          const mediaId = item.dataset.mediaId;
          const featuredMediaId = variant?.featured_media?.id;

          if (featuredMediaId) {
            const numericMediaId = parseInt(mediaId, 10);
            const exclusiveIds = this._variantExclusiveMediaIds || [];
            if (numericMediaId === featuredMediaId) {
              variantMatched.push(item);
            } else if (!exclusiveIds.includes(numericMediaId)) {
              general.push(item);
            } else {
              otherVariant.push(item);
            }
          } else if (!altText.includes('|')) {
            general.push(item);
          } else if (this.shouldShowMediaForVariant(altText, variantOptions, variant, mediaId)) {
            variantMatched.push(item);
          } else {
            otherVariant.push(item);
          }
        });

        // Append in desired order: featured (alt mode) → variant-matched → general → other-variant
        // appendChild() moves nodes in-place, so this is a single batch reorder
        [...featured, ...variantMatched, ...general, ...otherVariant].forEach(item => {
          mediaList.appendChild(item);
        });

        // Mirror the same order in the thumbnails list (matched by media ID)
        if (thumbList) {
          const allThumbs = Array.from(
            thumbList.querySelectorAll('.product__thumbs-item:not(.swiper-slide-duplicate)')
          );
          const thumbMap = new Map(allThumbs.map(t => [t.dataset.target, t]));
          const orderedIds = [...featured, ...variantMatched, ...general, ...otherVariant].map(
            item => item.dataset.mediaId
          );
          orderedIds.forEach(id => {
            const thumb = thumbMap.get(id);
            if (thumb) thumbList.appendChild(thumb);
          });
        }

        // Re-index visible items so grid layout positions reflect the new order
        this.reindexVisibleItems();
        this.repositionPeopleInterestedOverlay();
      }

      repositionPeopleInterestedOverlay() {
        const overlay = this.querySelector('.product__people-interested-media-overlay');
        if (!overlay) return;

        // Find the first visible (non-hidden) media item
        const firstVisible = Array.from(
          this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')
        ).find(item =>
          !item.classList.contains('variant-hidden') &&
          !item.hasAttribute('data-hidden-by-show-more') &&
          item.style.display !== 'none'
        );

        if (!firstVisible) return;

        // If the overlay is already inside the first visible item, nothing to do
        if (firstVisible.contains(overlay)) return;

        // Move the overlay into the first visible item's container
        const targetContainer = firstVisible.querySelector('.product__media-item-container');
        if (targetContainer) {
          targetContainer.appendChild(overlay);
        }
      }

      /*
       * Alt-text tag matching — used when the theme setting
       * "Product media gallery → Show variant images by alt-text tag" is on.
       * Mirrors snippets/variant-media-alt-tags.liquid 1:1 (keep both in sync):
       *   "Product name | Value A, Value B, Value C | Wert A, Wert B, Wert C"
       * - text before the first "|" = product name; every later "|" part is one tag group
       *   (one per language, since alt text can't be translated); any group may match;
       *   no non-empty group → general image (all variants)
       * - every tag must equal one of the variant's option values (case-insensitive,
       *   whitespace collapsed, spaces around "/" ignored); tag order does not matter
       * - a full combination shows for that variant only, a partial tag ("Fix") for
       *   every variant carrying that value
       */
      _normalizeAltTag(value) {
        return String(value || '')
          .toLowerCase()
          .split(/[ \t\n\r\f\v]+/)
          .filter(Boolean)
          .join(' ')
          .replace(/ \//g, '/')
          .replace(/\/ /g, '/');
      }

      // Alt mode: video / 3D model slides are always general, whatever their poster's alt says
      // (same as the Liquid passes). Legacy mode keeps its original img-based check.
      _isAltModeNonImage(item) {
        return this.variantMediaMode === 'alt' && !!item.dataset.mediaType && item.dataset.mediaType !== 'image';
      }

      // Alt mode: the selected variant's featured image (Shopify admin) is always shown and
      // placed first, whatever its alt text says — language-independent, so it still works
      // where option values are translated but the alt-text tags exist in one language only.
      _isVariantFeaturedMedia(mediaId, variant) {
        const featuredId = variant?.featured_media?.id;
        return !!featuredId && parseInt(mediaId, 10) === Number(featuredId);
      }

      // Returns one normalized tag list per group (text before the first "|" is the product
      // name, every later "|" part is a group — one per language), or null for a general image.
      _parseAltTags(altText) {
        if (!altText || !altText.includes('|')) return null;
        const groups = altText
          .split('|')
          .slice(1)
          .map(group => group.split(',').map(tag => this._normalizeAltTag(tag)).filter(Boolean))
          .filter(tags => tags.length);
        return groups.length ? groups : null;
      }

      // Variant match keys: the normalized SKU, plus one comma-wrapped key per option value
      // (",fix," / ",table/wide,"); values that themselves contain commas become a
      // multi-segment key so they still match.
      _variantAltKeys(variant) {
        if (!variant) return { sku: '', options: [] };
        const options = [variant.option1, variant.option2, variant.option3]
          .map(value => String(value || '').split(',').map(part => this._normalizeAltTag(part)).filter(Boolean))
          .filter(parts => parts.length)
          .map(parts => `,${parts.join(',')},`);
        return { sku: this._normalizeAltTag(variant.sku), options };
      }

      // True when at least one group matches the variant:
      // - SKU group (any tag is a SKU of this product): the variant's SKU is one of its tags
      // - option-value group: the variant's option values consume every tag
      _altTagsMatch(groups, keys) {
        const productSkus = this._productSkus || new Set();
        return groups.some(tags => {
          if (tags.some(tag => productSkus.has(tag))) {
            return !!keys.sku && tags.includes(keys.sku);
          }
          let remaining = `,${tags.join(',')},`;
          keys.options.forEach(key => {
            if (remaining.includes(key)) remaining = remaining.replace(key, ',');
          });
          return remaining === ',';
        });
      }

      shouldShowMediaForVariant(altText, variantOptions, variant, mediaId) {
        if (this.variantMediaMode === 'alt') {
          if (this._isVariantFeaturedMedia(mediaId, variant)) return true;
          const tags = this._parseAltTags(altText);
          if (!tags) return true;
          return this._altTagsMatch(tags, this._variantAltKeys(variant));
        }

        const featuredMediaId = variant?.featured_media?.id;

        // Primary: Shopify variant featured_media (language-independent)
        if (featuredMediaId) {
          const numericMediaId = parseInt(mediaId, 10);
          if (numericMediaId === featuredMediaId) return true;

          const exclusiveIds = this._variantExclusiveMediaIds || [];
          if (!exclusiveIds.includes(numericMediaId)) return true;

          return false;
        }

        // Fallback: alt-text pipe matching when variant has no featured image
        // If alt text is empty, show the image (default/fallback images)
        if (!altText || altText.trim() === '') {
          return true;
        }

        // Check if alt text contains the separator "|"
        // Format: "Descriptive text | variant1,variant2,variant3"
        // If no "|" is present, show the image for all variants (general/descriptive images)
        if (!altText.includes('|')) {
          return true;
        }

        // Split by "|" and get the variant matching part (after the separator)
        const parts = altText.split('|');
        const variantMatchingText = parts.length > 1 ? parts[1].trim() : '';
        
        // If no text after "|", show for all variants
        if (!variantMatchingText) {
          return true;
        }

        // Normalize the variant matching text for comparison
        const normalizedAlt = variantMatchingText.toLowerCase().trim();
        
        // ALL variant options must be present in the variant matching text
        // This ensures "fix, mit Occhio Air" won't show for "fix, ohne Occhio Air"
        const allOptionsMatch = variantOptions.every(option => {
          const normalizedOption = option.toLowerCase().trim();
          
          // Check if this specific option is in the alt text
          // Direct contains match (e.g., alt "fix, mit Occhio Air" contains "fix")
          if (normalizedAlt.includes(normalizedOption)) return true;
          
          // Also check word-by-word matching for multi-word options
          // This handles cases like "mit Occhio Air" or "ohne Occhio Air"
          const altWords = normalizedAlt.split(/[\s,\-–—_\/]+/).filter(word => word.length > 0);
          const optionWords = normalizedOption.split(/[\s,\-–—_\/]+/).filter(word => word.length > 0);
          
          // For multi-word options, check if all words are present in alt text
          if (optionWords.length > 1) {
            const allWordsPresent = optionWords.every(optionWord => {
              return altWords.some(altWord => {
                // Exact word match
                if (altWord === optionWord) return true;
                // Word contains option word (for partial matches like "display" in "displays")
                if (altWord.includes(optionWord) && optionWord.length > 2) return true;
                return false;
              });
            });
            if (allWordsPresent) return true;
          } else {
            // For single-word options, check if the word is in alt text
            const singleWordMatch = altWords.some(altWord => {
              // Exact word match
              if (altWord === normalizedOption) return true;
              // Word contains option (for partial matches)
              if (altWord.includes(normalizedOption) && normalizedOption.length > 2) return true;
              return false;
            });
            if (singleWordMatch) return true;
          }
          
          return false;
        });

        return allOptionsMatch;
      }

      reindexVisibleItems() {
        // Get all media items and re-index only visible ones (not hidden by variant filtering)
        const allMediaItems = this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)');
        const allThumbnailItems = this.querySelectorAll('.product__thumbs-item');
        let visibleIndex = 0;

        // First pass: index all items that aren't hidden by variant filtering
        allMediaItems.forEach((item, index) => {
          // Check if item is hidden by variant filtering
          // Items hidden by variant have variant-hidden class
          const isHiddenByVariant = item.classList.contains('variant-hidden');
          
          if (!isHiddenByVariant) {
            visibleIndex++;
            item.setAttribute('data-visible-index', visibleIndex);
            item.classList.add('variant-visible');
            item.classList.remove('variant-hidden');
            
            // Also update corresponding thumbnail if it exists
            if (allThumbnailItems[index]) {
              allThumbnailItems[index].setAttribute('data-visible-index', visibleIndex);
            }
          } else {
            // Remove index and grid classes if hidden by variant filtering
            item.removeAttribute('data-visible-index');
            item.classList.remove('variant-visible', 'grid-pos-1-2', 'grid-pos-3-5', 'grid-pos-6-7', 'grid-pos-8-10', 'grid-width-50', 'grid-width-33', 'grid-width-100');
            item.style.removeProperty('width');
            // Also clean up show more button state
            item.removeAttribute('data-hidden-by-show-more');
            
            // Also remove index from corresponding thumbnail
            if (allThumbnailItems[index]) {
              allThumbnailItems[index].removeAttribute('data-visible-index');
            }
          }
        });

        // Handle show more button visibility for grid-mix-2-3 layout
        // This will also call updateGridPositions internally
        this.updateShowMoreButton();
      }

      updateGridPositions() {
        // Only apply grid position classes to items that are actually displayed
        // (not hidden by show more button or variant filtering)
        const isGridMix23 = this.mediaLayout === 'grid-mix-2-3';
        const isGridMix21 = this.mediaLayout === 'grid-mix-2-1';
        if (!isGridMix23 && !isGridMix21) return;

        // Use requestAnimationFrame to ensure DOM updates are complete
        requestAnimationFrame(() => {
          const allMediaItems = this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)');

          // First, remove all grid position and width classes to start fresh
          allMediaItems.forEach(item => {
            item.classList.remove('grid-pos-1-2', 'grid-pos-3-5', 'grid-pos-6-7', 'grid-pos-8-10', 'grid-width-50', 'grid-width-33', 'grid-width-100');
            // Remove inline width that might interfere
            item.style.removeProperty('width');
          });

          // Get only the items that are actually displayed (not hidden by variant or show more)
          const displayedItems = Array.from(allMediaItems).filter(item => {
            return !item.classList.contains('variant-hidden') &&
                   !item.hasAttribute('data-hidden-by-show-more') &&
                   item.style.display !== 'none';
          });

          if (isGridMix23) {
            // Pattern: positions 1-2 get 50% width (2 per row), positions 3-5 get 33.333% width (3 per row)
            // Then repeat the pattern for positions 6-10, 11-15, etc.
            displayedItems.forEach((item, index) => {
              const displayedPosition = index + 1; // 1-based position
              
              // Calculate position in 5-item cycle (1-5, 6-10, 11-15, etc.)
              const cyclePosition = ((displayedPosition - 1) % 5) + 1;
              
              if (cyclePosition === 1 || cyclePosition === 2) {
                item.classList.add('grid-pos-1-2', 'grid-width-50');
              } else if (cyclePosition === 3 || cyclePosition === 4 || cyclePosition === 5) {
                item.classList.add('grid-pos-3-5', 'grid-width-33');
              }
            });
          } else if (isGridMix21) {
            // Pattern repeats every 3 visible items: 50% / 50% / 100%
            // By counting only displayed items, hidden items no longer affect the cycle.
            displayedItems.forEach((item, index) => {
              const displayedPosition = index + 1; // 1-based position

              // Calculate position in 3-item cycle (1-3, 4-6, 7-9, etc.)
              const cyclePosition = ((displayedPosition - 1) % 3) + 1;

              if (cyclePosition === 1 || cyclePosition === 2) {
                item.classList.add('grid-width-50');
              } else {
                item.classList.add('grid-width-100');
              }
            });
          }
        });
      }

      updateShowMoreButton() {
        // Handle "show more" for grid-mix-2-3 and grid-mix-2-1 layouts
        const isGridMix23 = this.mediaLayout === 'grid-mix-2-3';
        const isGridMix21 = this.mediaLayout === 'grid-mix-2-1';
        if (!isGridMix23 && !isGridMix21) return;

        // Get ALL media items (to reset their state first)
        const allMediaItems = this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)');

        // Below the desktop toggle breakpoint, always show all media items and hide the toggle.
        // The toggle is styled for desktop only via CSS (min-width: 1025px), so without this
        // guard tablet widths (e.g. iPad Pro at 1024px) would hide images with no way to expand.
        if (!this.isDesktopMediaToggleViewport()) {
          allMediaItems.forEach(item => {
            const isVariantHidden =
              item.classList.contains('variant-hidden') ||
              (item.style.display === 'none' && !item.hasAttribute('data-hidden-by-show-more'));

            if (!isVariantHidden) {
              item.style.display = '';
              item.style.visibility = '';
              item.removeAttribute('data-hidden-by-show-more');
              item.classList.remove('grid-width-50', 'grid-width-33', 'grid-width-100');
              item.style.removeProperty('width');
            }
          });

          const toggleButtonMobile = this.querySelector('.product__media-toggle-btn');
          if (toggleButtonMobile) {
            toggleButtonMobile.style.display = 'none';
            toggleButtonMobile.classList.add('hidden');
            toggleButtonMobile.setAttribute('aria-expanded', false);
          }

          // No need to apply desktop-specific show-more logic below the toggle breakpoint.
          return;
        }

        // First, reset all items that aren't variant-hidden
        // Also remove width classes so they can be recalculated fresh
        allMediaItems.forEach(item => {
          const isVariantHidden =
            item.classList.contains('variant-hidden') ||
            (item.style.display === 'none' && !item.hasAttribute('data-hidden-by-show-more'));

          if (!isVariantHidden && item.hasAttribute('data-visible-index')) {
            // Reset display for items that should be considered
            item.style.display = '';
            item.style.visibility = '';
            item.removeAttribute('data-hidden-by-show-more');
            // Remove width classes so updateGridPositions can recalculate
            item.classList.remove('grid-width-50', 'grid-width-33', 'grid-width-100');
            item.style.removeProperty('width');
          }
        });

        // Now get only the items that are visible after variant filtering
        // IMPORTANT: We use Array.from() to get the actual array of visible items
        // This ensures we can iterate by array position (0, 1, 2...) rather than DOM position
        // So if images 1-2 are hidden by variant, images 3-9 become array [0-6]
        const visibleItems = Array.from(this.querySelectorAll('.product__media-item[data-visible-index]:not(.swiper-slide-duplicate):not(.variant-hidden)')).filter(item => {
          // Additional check: make sure the item isn't already hidden by variant filtering via inline style
          return item.style.display !== 'none' || item.hasAttribute('data-hidden-by-show-more');
        });
        
        const visibleCount = visibleItems.length;
        // Show 7 images first for 2-3 layout, 5 images first for 2-1 layout
        const maxVisible = isGridMix23 ? 7 : 5;
        const isExpanded = this.classList.contains('media-gallery--expanded');

        // Show/hide items based on expanded state and visible count
        // We iterate through the ACTUAL visible items array, not by data-visible-index
        // This ensures the first N VISIBLE items are shown, regardless of their original DOM position
        visibleItems.forEach((item, arrayIndex) => {
          const actualPosition = arrayIndex + 1; // Position in the visible items array (1-based)
          
          if (!isExpanded && actualPosition > maxVisible) {
            // Hide items beyond the first 7 visible items when collapsed
            item.style.display = 'none';
            item.style.visibility = 'hidden';
            item.setAttribute('data-hidden-by-show-more', 'true');
          } else {
            // Show items when expanded or within first 7 visible
            item.style.display = '';
            item.style.visibility = '';
            item.removeAttribute('data-hidden-by-show-more');
          }
        });

        // Recalculate grid positions after showing/hiding items
        this.updateGridPositions();

        // Update toggle button visibility and state
        const toggleButton = this.querySelector('.product__media-toggle-btn');
        if (toggleButton) {
          const showText = toggleButton.querySelector('[data-toggle-text-show]');
          const hideText = toggleButton.querySelector('[data-toggle-text-hide]');
          
          if (visibleCount > maxVisible) {
            // Show the button if there are more visible items than the initial limit
            toggleButton.style.display = '';
            toggleButton.classList.remove('hidden');
            
            // Update button text and aria-expanded based on current state
            toggleButton.setAttribute('aria-expanded', isExpanded);
            if (showText && hideText) {
              if (isExpanded) {
                showText.classList.add('hidden');
                hideText.classList.remove('hidden');
              } else {
                showText.classList.remove('hidden');
                hideText.classList.add('hidden');
              }
            }
          } else {
            // Hide the button if there are 7 or fewer visible items
            toggleButton.style.display = 'none';
            toggleButton.classList.add('hidden');
          }
        }
      }

      updateSliderAfterFilter() {
        // Use requestAnimationFrame to ensure DOM updates are complete
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            // Update slider if it exists
            if (this.sliderInstance && typeof this.sliderInstance === 'object' && this.sliderInstance.slider) {
              try {
                // Force slider to recalculate visible slides
                this.sliderInstance.slider.update();

                // Find the first visible slide by checking both the slide element itself
                // and the .product__media-item inside it
                const slides = this.sliderInstance.slider.slides || [];
                let firstVisibleIndex = -1;
                
                for (let i = 0; i < slides.length; i++) {
                  const slide = slides[i];
                  // Skip duplicate slides created by Swiper
                  if (slide.classList.contains('swiper-slide-duplicate')) {
                    continue;
                  }
                  
                  // Check if slide itself is visible
                  const slideStyle = window.getComputedStyle(slide);
                  if (slideStyle.display === 'none') {
                    continue;
                  }
                  
                  // Check if the media item inside is visible
                  const mediaItem = slide.querySelector('.product__media-item');
                  if (mediaItem) {
                    const mediaStyle = window.getComputedStyle(mediaItem);
                    const isHidden = mediaItem.classList.contains('hidden') || 
                                   mediaItem.classList.contains('variant-hidden') ||
                                   mediaItem.style.display === 'none' ||
                                   mediaStyle.display === 'none';
                    
                    if (!isHidden) {
                      firstVisibleIndex = i;
                      break;
                    }
                  }
                }
                
                // If we found a visible slide and we're not already on it, slide to it
                if (firstVisibleIndex >= 0) {
                  this.sliderInstance.slider.slideTo(firstVisibleIndex, 0, false);
                }
              } catch (e) {
                console.warn('Error updating slider after filter:', e);
              }
            }

            // Update thumbnails slider if it exists
            if (this.thumbsInstance && typeof this.thumbsInstance === 'object' && this.thumbsInstance.slider) {
              try {
                this.thumbsInstance.slider.update();
                
                // Also reset thumbnails to first position
                if (this.thumbsInstance.slider.slides && this.thumbsInstance.slider.slides.length > 0) {
                  this.thumbsInstance.slider.slideTo(0, 0, false);
                }
              } catch (e) {
                console.warn('Error updating thumbnails slider after filter:', e);
              }
            }
          });
        });
      }

      sortMediaItems(variant, featuredMedia) {
        if (!featuredMedia) {
          featuredMedia =
            variant.featured_media ||
            (variant.featured_image
              ? {
                  id: variant.featured_image.id,
                  position: variant.featured_image.position || 0,
                  media_type: 'image',
                }
              : null);
        }

        if (!featuredMedia) return;

        let newMedias = this.elements.mediaItems;

        // Reset ordering.
        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        newMedias.some((media, index) => {
          if (media.dataset.mediaId == featuredMedia.id) {
            const [element] = newMedias.splice(index, 1);
            newMedias.unshift(element);
            return true;
          }
        });

        this.elements.mediaList.innerHTML = '';
        newMedias.forEach((media) => {
          this.elements.mediaList.appendChild(media);
        });

        if (!window.matchMedia('(max-width: 1024px)').matches && this.context !== 'quickview') {
          const selectedMedia = this.querySelector(`[data-media-id="${featuredMedia.id}"]`);
          if (selectedMedia) {
            window.scrollTo({ top: selectedMedia.offsetTop, behavior: 'smooth' });
          }
        }
      }

      initMediaToggle() {
        if (this.mediaToggleInitialized) return;

        const toggleButton = this.querySelector('.product__media-toggle-btn');
        if (!toggleButton) return;

        toggleButton.addEventListener('click', (e) => {
          e.preventDefault();
          this.handleMediaToggle(toggleButton);
        });

        this.mediaToggleInitialized = true;
      }

      handleMediaToggle(button) {
        const wasExpanded = this.classList.contains('media-gallery--expanded');
        const isExpanded = this.classList.toggle('media-gallery--expanded');
        const showText = button.querySelector('[data-toggle-text-show]');
        const hideText = button.querySelector('[data-toggle-text-hide]');

        // Store current scroll position before DOM updates (when expanding)
        let savedScrollPosition = null;
        if (isExpanded && !wasExpanded) {
          savedScrollPosition = window.pageYOffset || window.scrollY || document.documentElement.scrollTop;
        }

        // Update aria-expanded
        button.setAttribute('aria-expanded', isExpanded);

        // Toggle text visibility
        if (showText && hideText) {
          if (isExpanded) {
            showText.classList.add('hidden');
            hideText.classList.remove('hidden');
          } else {
            showText.classList.remove('hidden');
            hideText.classList.add('hidden');
          }
        }

        // Update show more button visibility after toggle
        // This will also recalculate grid positions
        this.updateShowMoreButton();

        // Handle scrolling behavior after DOM updates
        if (isExpanded && !wasExpanded) {
          // When expanding (showing more images): restore scroll position to stay in place
          // Use double requestAnimationFrame to ensure all DOM updates are complete
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (savedScrollPosition !== null) {
                window.scrollTo({
                  top: savedScrollPosition,
                  behavior: 'auto' // Instant scroll to prevent visual jump
                });
              }
            });
          });
        } else if (!isExpanded && wasExpanded) {
          // When collapsing (showing less images): scroll to center the button in viewport
          // Use double requestAnimationFrame to ensure all DOM updates are complete
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              // Get button position to calculate centering
              const buttonRect = button.getBoundingClientRect();
              const buttonTop = buttonRect.top + window.pageYOffset;
              const viewportHeight = window.innerHeight;
              const buttonHeight = buttonRect.height;
              
              // Calculate scroll position to center the button vertically in the viewport
              const scrollPosition = buttonTop - (viewportHeight / 2) + (buttonHeight / 2);
              
              // Smooth scroll to position (ensure it's not negative)
              window.scrollTo({
                top: Math.max(0, scrollPosition),
                behavior: 'smooth'
              });
            });
          });
        }
      }
    }
  );
}
